import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const S = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-heading text-2xl text-navy">{title}</h2>
    <div className="mt-3 space-y-3 text-[15px] text-[#57534E] leading-relaxed">{children}</div>
  </section>
);

const EMAIL = (
  <a href="mailto:andrea@mybestbodyclub.com" className="text-teal font-medium">andrea@mybestbodyclub.com</a>
);

export default function Terms() {
  usePageTitle("Terms & Conditions");
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-28 pb-20" data-testid="terms-page">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">My Best Body Club</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-navy">Terms &amp; Conditions of Membership</h1>
        <p className="mt-4 text-sm text-[#78716C]">Last updated: August 2026 · My Best Body Club, United Kingdom</p>

        <p className="mt-8 text-[15px] text-[#57534E] leading-relaxed">
          These Terms &amp; Conditions ("Terms") govern your access to and use of the My Best Body Club digital
          platform, member content library, masterclasses, and any associated services (the "Service"), operated
          by My Best Body Club ("MBBC", "we", "us", "our"). By creating an account or subscribing to the Service,
          you ("Member", "you", "your") agree to be bound by these Terms.
        </p>

        <S title="1. Definitions">
          <p>
            <strong>"Content"</strong> means all material made available through the Service, including but not
            limited to the Member Handbook, recipe libraries, meal planners, masterclass videos, PDFs, and any
            other written, visual, or audio material.
          </p>
          <p><strong>"Membership"</strong> means a paid, recurring subscription granting access to the Service.</p>
          <p>
            <strong>"Physical Hubs"</strong> means the in-person membership locations, run by qualified
            Nutritional Therapists, due to open from January 2027.
          </p>
          <p><strong>"Virtual Membership"</strong> means digital-only access to the Service, available from launch.</p>
        </S>

        <S title="2. Membership & Subscription">
          <p>
            2.1 Membership is offered on a recurring monthly subscription basis, billed in advance via our
            payment processor, Stripe.
          </p>
          <p>
            2.2 Your Membership begins on the date your first payment is successfully processed and automatically
            renews each month unless cancelled in accordance with Clause 4.
          </p>
          <p>
            2.3 We may, from time to time, introduce additional membership tiers (including Physical Hub
            membership from January 2027) or optional add-ons (such as the Virtual Masterclass Bolt-On). Any such
            changes will be clearly described at the point of purchase.
          </p>
          <p>2.4 You must be 18 years or older to hold a Membership.</p>
        </S>

        <S title="3. Payment & Billing">
          <p>3.1 All payments are processed securely through Stripe. We do not store your full card details.</p>
          <p>
            3.2 Membership fees are charged in advance on the same date each month (your "billing date"),
            corresponding to the date you first subscribed.
          </p>
          <p>
            3.3 If a payment fails, we will attempt to notify you and may make further attempts to process
            payment. We reserve the right to suspend access to the Service if payment is not successfully
            received within 7 days of the failed attempt.
          </p>
          <p>
            3.4 We reserve the right to change Membership pricing. We will give you at least 30 days' notice of
            any price increase by email before it takes effect. Continuing your Membership after this date
            constitutes acceptance of the new price; if you do not agree, you may cancel in accordance with
            Clause 4 before the new price takes effect.
          </p>
        </S>

        <S title="4. Cancellation & Refunds">
          <p>
            4.1 You may cancel your Membership at any time via your account settings, or by contacting us at{" "}
            {EMAIL}.
          </p>
          <p>
            4.2 Cancellation will take effect at the end of your current paid billing period. You will retain
            access to the Service until that date, and will not be charged again thereafter.
          </p>
          <p>4.3 We do not offer refunds for partial months or unused periods of Membership.</p>
          <p>
            4.4 Cooling-off period: Under the Consumer Contract Regulations 2013, you ordinarily have a 14-day
            right to cancel digital service contracts for a full refund. By subscribing and accessing Content
            immediately, you acknowledge and agree that you are expressly requesting immediate access and consent
            to waive this 14-day cancellation right once you have accessed any Content.
          </p>
          <p>
            4.5 Pausing your Membership: as an alternative to cancellation, you may pause your Membership from
            your member portal up to three times per calendar year, for one month at a time. While paused,
            billing and access to the Service are suspended, and both resume automatically at the end of the
            pause period (or earlier, if you choose to resume). Each pause counts towards your yearly allowance,
            even if you resume early.
          </p>
        </S>

        <S title="5. Content Usage & Intellectual Property">
          <p>
            5.1 All Content is owned by MBBC (or licensed to us) and is protected by copyright and other
            intellectual property laws.
          </p>
          <p>
            5.2 Your Membership grants you a personal, non-transferable, non-exclusive licence to access and use
            Content for your own personal, non-commercial use only.
          </p>
          <p>
            5.3 You must not: (a) copy, reproduce, redistribute, resell, or share Content with any person who is
            not an active Member; (b) use Content, in whole or in part, for the purposes of any other business,
            coaching practice, or commercial venture; or (c) systematically download or print Content with the
            intention of retaining access after your Membership ends.
          </p>
          <p>
            5.4 We reserve the right to apply reasonable technical measures (such as watermarking) to protect
            Content, and to suspend or terminate the Membership of any individual found to be in breach of this
            Clause 5.
          </p>
        </S>

        <S title="6. Health & Medical Disclaimer">
          <p>
            6.1 Content provided through the Service is intended for general educational and informational
            purposes only. It is not personalised medical, dietetic, or therapeutic advice, and is not a
            substitute for consultation with a qualified healthcare professional.
          </p>
          <p>
            6.2 You should consult your GP or an appropriate healthcare provider before making significant
            changes to your diet or lifestyle, particularly if you are pregnant or breastfeeding, have an
            existing medical condition, are taking medication, or have a history of disordered eating.
          </p>
          <p>
            6.3 While our guidance is delivered by qualified Nutritional Therapists and is intended to be
            evidence-based, individual results vary, and MBBC makes no guarantee of specific health or body
            composition outcomes.
          </p>
          <p>
            6.4 To the fullest extent permitted by law, MBBC accepts no liability for any adverse outcome arising
            from your use of, or reliance upon, Content, save where such outcome arises from our negligence.
          </p>
        </S>

        <S title="7. Data Protection & Privacy">
          <p>
            7.1 We process your personal data in accordance with UK data protection law (UK GDPR and the Data
            Protection Act 2018). Full details of what data we collect, why, and how it is used are set out in
            our separate{" "}
            <Link to="/privacy" className="text-teal font-medium">Privacy Policy</Link>, which forms part of
            these Terms.
          </p>
          <p>
            7.2 By subscribing to the Service, you consent to the processing of your data as described in our
            Privacy Policy.
          </p>
        </S>

        <S title="8. Community Guidelines">
          <p>
            8.1 Where the Service includes access to a community space (including but not limited to a private
            Facebook group or WhatsApp group), you agree to engage respectfully with other Members and MBBC staff.
          </p>
          <p>
            8.2 You must not share Content from the Service into any community space, or externally, other than
            for the purpose of your own personal engagement within that space.
          </p>
          <p>
            8.3 We reserve the right to remove any Member from a community space, without affecting the remainder
            of their Membership, for conduct we reasonably consider abusive, harassing, or otherwise inappropriate.
          </p>
        </S>

        <S title="9. Limitation of Liability">
          <p>
            9.1 To the fullest extent permitted by law, MBBC shall not be liable for any indirect, incidental, or
            consequential loss arising from your use of the Service, including but not limited to loss of
            profits, data, or opportunity.
          </p>
          <p>
            9.2 Nothing in these Terms excludes or limits our liability for death or personal injury caused by
            our negligence, for fraud, or for any other liability which cannot lawfully be excluded or limited.
          </p>
          <p>
            9.3 We do not guarantee that the Service will be uninterrupted or error-free, and we accept no
            liability for temporary unavailability of the Service due to maintenance, technical issues, or
            circumstances beyond our reasonable control.
          </p>
        </S>

        <S title="10. Changes to the Service">
          <p>
            10.1 We may, from time to time, add, modify, or remove Content or features of the Service, including
            as new elements (such as Physical Hubs or a future mobile app) become available.
          </p>
          <p>
            10.2 We will provide reasonable notice of any material reduction in the Service available to existing
            Members.
          </p>
        </S>

        <S title="11. Termination">
          <p>
            11.1 We may suspend or terminate your Membership, with immediate effect and without refund, if you
            breach these Terms — in particular Clause 5 (Content Usage &amp; Intellectual Property) or Clause 8
            (Community Guidelines).
          </p>
          <p>11.2 You may terminate your Membership at any time in accordance with Clause 4.</p>
        </S>

        <S title="12. General">
          <p>
            12.1 These Terms, together with our Privacy Policy, constitute the entire agreement between you and
            MBBC in relation to the Service.
          </p>
          <p>
            12.2 If any provision of these Terms is found to be unenforceable, the remaining provisions will
            continue in full force and effect.
          </p>
          <p>
            12.3 We may update these Terms from time to time. We will notify Members of any material changes by
            email in advance of them taking effect.
          </p>
        </S>

        <S title="13. Governing Law">
          <p>
            These Terms are governed by the laws of England and Wales, and any disputes shall be subject to the
            exclusive jurisdiction of the courts of England and Wales.
          </p>
        </S>

        <S title="14. Contact Us">
          <p>If you have any questions about these Terms, please contact us at {EMAIL}.</p>
        </S>
      </div>
      <Footer />
    </div>
  );
}
